import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  listAccounts,
  deleteAccount,
  listMessages,
  listDrafts,
  searchMessages,
  getMessage,
  setMessageFlags,
  setMessageNote,
  fetchMessageTrust,
  fetchMessageThread,
  checkLanguageQuality,
  agentAsk,
  undoSend,
  getSendStatus,
  oauthStart,
  fetchMe,
  fetchCalendarStatus,
  fetchContactsStatus,
  syncAccount,
  syncAllAccounts,
  uploadAccountAvatar,
  deleteAccountAvatar,
  ApiError,
  type MailAccount,
  type MessageSummary,
  type MessageDetail,
  type ImageDisplayMode,
} from '../lib/api'
import { LanguageMenu } from '../components/LanguageMenu'
import { ImageDisplaySettings } from '../components/ImageDisplaySettings'
import { MailBodyFrame } from '../components/MailBodyFrame'
import { useSession } from '../lib/useSession'
import {
  sanitizeEmailHtml,
  textToSafeHtml,
  countBlockedRemote,
  translationLooksGarbage,
} from '../lib/sanitize'
import {
  allowMessageImagesSession,
  allowSenderImagesAlways,
  allowSenderImagesSession,
  isMessageImagesSessionAllowed,
  isSenderImagesAlwaysAllowed,
  isSenderImagesSessionAllowed,
} from '../lib/imagePrefs'
import { useUiStore } from '../lib/uiStore'
import { Icon, type IconName } from '../components/Icon'
import { sanitizeAvatarColor } from '../lib/avatarContrast'
import { ComposeModal, type ComposeSeed } from '../components/ComposeModal'
import { AccountAvatar } from '../components/AccountAvatar'
import { AgentAvatar } from '../components/AgentAvatar'
import { AddAccountPanel } from '../components/AddAccountPanel'
import { SettingsModal } from '../components/SettingsModal'
import { UserAvatar } from '../components/UserAvatar'
import { UserMenu } from '../components/UserMenu'
import { ProfilePanel } from '../components/ProfilePanel'
import { CalendarPanel } from '../components/CalendarPanel'
import { ContactsPanel } from '../components/ContactsPanel'
import '../styles/mail-v3.css'
import '../styles/mail-responsive.css'

type ListTab = 'priority' | 'all' | 'accounts'
type ViewMode = 'original' | 'translation' | 'both'
type DrillView = 'home' | 'list' | 'reader'
type SecId = 'mail' | 'agent' | 'add' | 'settings' | 'soon' | 'acct' | 'profile' | 'calendar' | 'contacts'
type SoonKind = 'calendar' | 'contacts'
type FolderId = 'INBOX' | 'SENT' | 'DRAFT' | 'SNOOZED' | 'SPAM' | 'TRASH' | 'STARRED'

const PAGE_SIZE = 50

const FOLDER_API: Partial<Record<FolderId, string>> = {
  INBOX: 'INBOX',
  SENT: 'SENT',
  DRAFT: 'DRAFT',
  SPAM: 'Spam',
  TRASH: 'Papierkorb',
}

const FOLDERS: { id: FolderId; labelKey: string; icon: IconName }[] = [
  { id: 'INBOX', labelKey: 'mail.folderInbox', icon: 'inbox' },
  { id: 'STARRED', labelKey: 'mail.folderStarred', icon: 'star' },
  { id: 'SENT', labelKey: 'mail.folderSent', icon: 'send' },
  { id: 'DRAFT', labelKey: 'mail.folderDrafts', icon: 'draft' },
  { id: 'SNOOZED', labelKey: 'mail.folderSnoozed', icon: 'schedule' },
  { id: 'SPAM', labelKey: 'mail.folderSpam', icon: 'report' },
  { id: 'TRASH', labelKey: 'mail.folderTrash', icon: 'delete' },
]

/** Quick folder icons in the list toolbar (replaces Priority / All / Accounts tabs). */
const LIST_TOOL_FOLDERS: FolderId[] = ['INBOX', 'STARRED', 'SENT', 'DRAFT']

const ACCENT_FALLBACK = ['#ea4335', '#0078d4', '#00796b', '#6d28d9', '#c2410c', '#0f766e']

function providerColor(a: MailAccount): string {
  const p = (a.provider || '').toLowerCase()
  if (p === 'gmail' || p === 'google') return 'var(--acc-gmail)'
  if (p === 'microsoft' || p === 'outlook') return 'var(--acc-outlook)'
  if (p === 'yahoo') return '#6001d2'
  if (p === 'gmx') return '#1c449b'
  if (p === 'custom' || p === 'imap') return 'var(--acc-imap)'
  return 'var(--acc-7gc)'
}

function providerLetter(a: MailAccount): string {
  const p = (a.provider || '').toLowerCase()
  if (p === 'gmail' || p === 'google') return 'G'
  if (p === 'microsoft' || p === 'outlook') return 'O'
  if (p === 'yahoo') return 'Y'
  if (p === 'gmx') return 'X'
  if (p === 'custom' || p === 'imap') return 'F'
  return (a.label || a.username || '?').slice(0, 1).toUpperCase()
}

function formatWhen(iso: string | null): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    const now = new Date()
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    }
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  } catch {
    return ''
  }
}

function langChip(m: MessageSummary): string | null {
  const preview = m.body_translated_preview
  if (!preview && !m.translation_engine) return null
  if (preview && translationLooksGarbage(m.body_preview || '', preview)) return null
  if (!preview) return null
  const from = (m.lang_original || '?').toUpperCase().slice(0, 2)
  return `${from} → BS`
}

function parseViewMode(raw: string | null): ViewMode {
  if (raw === 'split' || raw === 'both') return 'both'
  if (raw === 'translation') return 'translation'
  return 'original'
}

function parseSec(raw: string | null): SecId {
  if (
    raw === 'agent' ||
    raw === 'add' ||
    raw === 'settings' ||
    raw === 'soon' ||
    raw === 'acct' ||
    raw === 'profile' ||
    raw === 'calendar' ||
    raw === 'contacts'
  )
    return raw
  return 'mail'
}

function AccountCalendarConnect({
  accountId,
  provider,
}: {
  accountId: string
  provider: string
}) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const statusQ = useQuery({
    queryKey: ['calendar-status'],
    queryFn: fetchCalendarStatus,
    staleTime: 15_000,
  })
  const row = statusQ.data?.accounts.find((a) => a.account_id === accountId)
  const connected = !!row?.calendar_connected

  async function connectCalendar() {
    setBusy(true)
    setErr(null)
    try {
      const p = (provider || '').toLowerCase()
      const oauthProvider =
        p.includes('gmail') || p.includes('google') ? 'gmail' : 'microsoft'
      const res = await oauthStart(oauthProvider, {
        purpose: 'calendar',
        account_id: accountId,
        write: false,
      })
      window.location.href = res.authorize_url
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'oauth failed')
      setBusy(false)
    }
  }

  return (
    <div>
      {connected ? (
        <p className="d" style={{ marginBottom: 8 }}>
          <em className="rd-cal-ok">{t('calendar.connected')}</em>
          {row?.calendar_write ? ` · ${t('calendar.writeReady')}` : ` · ${t('calendar.readReady')}`}
        </p>
      ) : (
        <p className="d" style={{ marginBottom: 8 }}>
          {t('calendar.connectHint')}
        </p>
      )}
      <button
        type="button"
        className="rd-rbtn2"
        disabled={busy || connected}
        onClick={() => void connectCalendar()}
      >
        {connected ? t('calendar.connected') : t('mail.connectCalendar')}
      </button>
      {err ? (
        <p className="d" role="status" style={{ marginTop: 8 }}>
          {err}
        </p>
      ) : null}
    </div>
  )
}

function AccountContactsConnect({
  accountId,
  provider,
}: {
  accountId: string
  provider: string
}) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const statusQ = useQuery({
    queryKey: ['contacts-status'],
    queryFn: fetchContactsStatus,
    staleTime: 15_000,
  })
  const row = statusQ.data?.accounts.find((a) => a.account_id === accountId)
  const connected = !!row?.contacts_connected

  async function connectContacts() {
    setBusy(true)
    setErr(null)
    try {
      const p = (provider || '').toLowerCase()
      const oauthProvider =
        p.includes('gmail') || p.includes('google') ? 'gmail' : 'microsoft'
      const res = await oauthStart(oauthProvider, {
        purpose: 'contacts',
        account_id: accountId,
        write: false,
      })
      window.location.href = res.authorize_url
    } catch {
      setErr(t('contacts.connectFailed'))
      setBusy(false)
    }
  }

  return (
    <div>
      {connected ? (
        <p className="d" style={{ marginBottom: 8 }}>
          <em className="rd-cal-ok">{t('contacts.connected')}</em>
          {` · ${t('contacts.readReady')}`}
        </p>
      ) : (
        <p className="d" style={{ marginBottom: 8 }}>
          {t('contacts.connectHint')}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="rd-rbtn2"
          disabled={busy}
          onClick={() => void connectContacts()}
        >
          {connected ? t('contacts.reconnect') : t('mail.connectContacts')}
        </button>
      </div>
      {err ? (
        <p className="d" role="status" style={{ marginTop: 8 }}>
          {err}
        </p>
      ) : null}
    </div>
  )
}

export function MailWorkspace() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useSession()
  const meQ = useQuery({ queryKey: ['auth-me'], queryFn: fetchMe, staleTime: 60_000 })
  const railDisplayName =
    (meQ.data?.display_name || user?.display_name || '').trim() ||
    (user?.email || meQ.data?.email || '').split('@')[0] ||
    t('profile.you')
  const { messageId } = useParams()
  const [params, setParams] = useSearchParams()
  const locale = useUiStore((s) => s.locale)
  const accountPrefs = useUiStore((s) => s.accountPrefs)
  const setAccountPref = useUiStore((s) => s.setAccountPref)

  const accountId = params.get('account') || ''
  const folder = (params.get('folder') as FolderId) || 'INBOX'
  const notesOnly = params.get('notes') === '1'
  const tab = (params.get('tab') as ListTab) || 'all'
  const view = parseViewMode(params.get('view'))
  const q = params.get('q') || ''
  const offset = Math.max(0, Number(params.get('offset') || 0) || 0)
  const sec = parseSec(params.get('sec') || (params.get('agent') === '1' ? 'agent' : null))
  const soonKind: SoonKind = params.get('soon') === 'contacts' ? 'contacts' : 'calendar'
  const acctId = sec === 'acct' ? params.get('acct') || '' : ''

  const [searchLocal, setSearchLocal] = useState(q)
  const [loadRemoteOnce, setLoadRemoteOnce] = useState(false)
  // Folders overlay — default closed; hamburger (.forNav) toggles on all viewports
  const [navShow, setNavShow] = useState(false)
  const navRef = useRef<HTMLElement | null>(null)
  const [drillView, setDrillView] = useState<DrillView>(() => {
    if (messageId) return 'reader'
    // Survive hard refresh on list URLs (account/folder/q) — do not reset to home
    if (accountId || params.get('folder') || q || offset > 0) return 'list'
    // Default inbox list (avoids folder-home flash left of rail on first paint)
    return 'list'
  })
  const [openAcc, setOpenAcc] = useState<Record<string, boolean>>({ all: true })
  const [compose, setCompose] = useState<ComposeSeed | null>(null)
  const [undoBar, setUndoBar] = useState<{ id: string; until: number } | null>(null)
  const [agentQ, setAgentQ] = useState('')
  const [agentAns, setAgentAns] = useState<string | null>(null)
  const [agentSources, setAgentSources] = useState<{ type: string; id?: string; subject?: string }[]>([])
  const [agentMeta, setAgentMeta] = useState<{ why?: string; privacy?: string; location?: string } | null>(
    null,
  )
  const [agentBusy, setAgentBusy] = useState(false)
  const [agentScope, setAgentScope] = useState<string | undefined>(undefined)
  const [agentReturnTo, setAgentReturnTo] = useState<SecId | null>(null)
  const [identityOpen, setIdentityOpen] = useState(false)
  const [noteEditorOpen, setNoteEditorOpen] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const noteOpenIntentRef = useRef(false)
  const [noteBusy, setNoteBusy] = useState(false)
  const [sendOptsOpen, setSendOptsOpen] = useState(() => {
    try {
      return sessionStorage.getItem('7gcmail.sendOptsOpen') === '1'
    } catch {
      return false
    }
  })
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [avatarRevision, setAvatarRevision] = useState(0)
  const [acctAvatarRevision, setAcctAvatarRevision] = useState(0)
  const [acctPhotoBusy, setAcctPhotoBusy] = useState(false)
  const [acctPhotoMsg, setAcctPhotoMsg] = useState<string | null>(null)
  const [undoTick, setUndoTick] = useState(0)
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const userMenuBtnRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    setSearchLocal(q)
  }, [q])

  // OAuth return — stay in modern workspace; never show raw IdP codes.
  const [oauthBanner, setOauthBanner] = useState<{ kind: 'ok' | 'warn'; text: string } | null>(
    () => {
      const err = params.get('oauth_error')
      if (err === 'oauth_denied') return { kind: 'warn', text: t('auth.oauthDenied') }
      if (err) return { kind: 'warn', text: t('auth.oauthFailedGeneric') }
      if (params.get('contacts_linked') === '1') return { kind: 'ok', text: t('contacts.oauthLinked') }
      if (params.get('calendar_linked') === '1') return { kind: 'ok', text: t('calendar.oauthLinked') }
      if (params.get('linked') === '1') return { kind: 'ok', text: t('auth.oauthLinked') }
      return null
    },
  )

  useEffect(() => {
    if (
      !params.get('oauth_error') &&
      params.get('contacts_linked') !== '1' &&
      params.get('calendar_linked') !== '1' &&
      params.get('linked') !== '1'
    ) {
      return
    }
    const next = new URLSearchParams(params)
    ;['oauth_error', 'contacts_linked', 'calendar_linked', 'calendar_write', 'linked'].forEach((k) =>
      next.delete(k),
    )
    setParams(next, { replace: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- one-shot strip OAuth flash params

  // ESC + click-outside close folders overlay
  useEffect(() => {
    if (!navShow) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setNavShow(false)
    }
    function onPointer(e: MouseEvent | PointerEvent) {
      const t = e.target as Node | null
      if (!t) return
      if (navRef.current?.contains(t)) return
      if ((t as Element).closest?.('.rd-back.forNav')) return
      setNavShow(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer, true)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer, true)
    }
  }, [navShow])

  const accountsQ = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
    staleTime: 20_000,
  })
  const accounts = accountsQ.data?.items || []

  const messagesQ = useQuery({
    queryKey: ['messages-v3', accountId, folder, q, offset, tab, notesOnly],
    queryFn: async () => {
      if (q.trim().length >= 2) {
        return searchMessages({
          q: q.trim(),
          account_id: accountId || undefined,
          limit: PAGE_SIZE,
          offset,
        })
      }
      if (folder === 'SNOOZED') {
        return { count: 0, total: 0, limit: PAGE_SIZE, offset, items: [] as MessageSummary[] }
      }
      const starred = folder === 'STARRED' && !notesOnly
      return listMessages({
        account_id: accountId || undefined,
        folder: starred || notesOnly ? undefined : FOLDER_API[folder],
        starred_only: starred || undefined,
        noted_only: notesOnly || undefined,
        priority_only: tab === 'priority' || undefined,
        limit: PAGE_SIZE,
        offset,
      })
    },
    enabled: folder !== 'DRAFT',
    staleTime: 10_000,
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
  })

  const draftsQ = useQuery({
    queryKey: ['drafts'],
    queryFn: listDrafts,
    staleTime: 15_000,
  })

  const msgQ = useQuery({
    queryKey: ['message', messageId],
    queryFn: () => getMessage(messageId!),
    enabled: Boolean(messageId),
    staleTime: 60_000,
  })

  // Soft sync while viewing mail — backend poll is ~5 min; UI list had no auto-refetch.
  useEffect(() => {
    if (sec !== 'mail') return
    let cancelled = false
    const run = async () => {
      try {
        if (accountId) {
          const res = await syncAccount(accountId)
          if (cancelled) return
          if ((res.inserted || 0) > 0 || (res.fetched || 0) > 0 || res.ok !== false) {
            await queryClient.invalidateQueries({ queryKey: ['messages-v3'] })
            await queryClient.invalidateQueries({ queryKey: ['accounts'] })
          }
        } else {
          const res = await syncAllAccounts()
          if (cancelled) return
          if ((res.inserted_total || 0) > 0 || (res.accounts_total || 0) > 0) {
            await queryClient.invalidateQueries({ queryKey: ['messages-v3'] })
            await queryClient.invalidateQueries({ queryKey: ['accounts'] })
          }
        }
      } catch {
        /* ignore — poll job remains source of truth */
      }
    }
    const t = window.setTimeout(() => void run(), 400)
    const iv = window.setInterval(() => void run(), 90_000)
    return () => {
      cancelled = true
      window.clearTimeout(t)
      window.clearInterval(iv)
    }
  }, [sec, accountId, queryClient])

  const trustQ = useQuery({
    queryKey: ['trust', messageId],
    queryFn: () => fetchMessageTrust(messageId!),
    enabled: Boolean(messageId),
  })

  const qualityQ = useQuery({
    queryKey: ['lang-quality', messageId],
    queryFn: () => checkLanguageQuality(messageId!),
    enabled: Boolean(messageId && msgQ.data?.body_translated),
  })

  useEffect(() => {
    setIdentityOpen(false)
  }, [messageId])

  useEffect(() => {
    try {
      sessionStorage.setItem('7gcmail.sendOptsOpen', sendOptsOpen ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [sendOptsOpen])

  /** Ustav §40: silent when OK/unknown; loud on fail/warn — never hide risk behind a closed accordion. */
  const identityRisk = useMemo(() => {
    const badges = trustQ.data?.badges || []
    return badges.some((b) => b.state === 'fail' || b.state === 'warn')
  }, [trustQ.data?.badges])

  useEffect(() => {
    if (identityRisk) setIdentityOpen(true)
  }, [messageId, identityRisk])

  const labelById = useMemo(() => {
    const m = new Map<string, MailAccount>()
    for (const a of accounts) m.set(a.id, a)
    return m
  }, [accounts])

  const totalUnread = accounts.reduce((s, a) => s + (a.unread_count || 0), 0)

  function resolveColor(a: MailAccount, idx: number): string {
    const fallback = providerColor(a) || ACCENT_FALLBACK[idx % ACCENT_FALLBACK.length]
    const pref = accountPrefs[a.id]?.color
    if (pref) return sanitizeAvatarColor(pref, fallback.startsWith('#') ? fallback : '#6d28d9')
    return fallback
  }

  function resolveInitial(a: MailAccount): string {
    const pref = accountPrefs[a.id]?.initial?.trim()
    if (pref) return pref.slice(0, 2).toUpperCase()
    return providerLetter(a)
  }

  function resolveLabel(a: MailAccount): string {
    return accountPrefs[a.id]?.label?.trim() || a.label || a.username
  }

  /** Server already filtered + paginated; do not re-filter the page client-side. */
  const filtered = useMemo(() => {
    if (folder === 'DRAFT') return [] as MessageSummary[]
    return messagesQ.data?.items || []
  }, [messagesQ.data, folder])

  const draftItems = useMemo(() => {
    let items = draftsQ.data?.items || []
    if (accountId) items = items.filter((d) => d.account_id === accountId)
    const needle = (q || searchLocal).trim().toLowerCase()
    if (needle) {
      items = items.filter((d) => {
        const hay = `${d.to_addrs || ''} ${d.subject || ''} ${d.body_text || ''}`.toLowerCase()
        return hay.includes(needle)
      })
    }
    return items
  }, [draftsQ.data, accountId, q, searchLocal])

  /** Flat list collapsed by thread_id (newest message represents the thread). */
  const listRows = useMemo(() => {
    const seen = new Set<string>()
    const rows: Array<MessageSummary & { threadCount: number }> = []
    const byThread = new Map<string, MessageSummary[]>()
    for (const m of filtered) {
      const tid = m.thread_id || m.id
      const arr = byThread.get(tid) || []
      arr.push(m)
      byThread.set(tid, arr)
    }
    for (const m of filtered) {
      const tid = m.thread_id || m.id
      if (seen.has(tid)) continue
      seen.add(tid)
      const group = byThread.get(tid) || [m]
      rows.push({ ...m, threadCount: group.length })
    }
    return rows
  }, [filtered])

  const threadQ = useQuery({
    queryKey: ['thread', messageId],
    queryFn: () => fetchMessageThread(messageId!),
    enabled: Boolean(messageId),
  })

  const activeAccount = accountId ? labelById.get(accountId) : null

  // Gmail-like: show list row immediately while full body loads (no blank reader).
  const listShell = useMemo((): MessageDetail | null => {
    if (!messageId) return null
    const m = listRows.find((row) => row.id === messageId)
    if (!m) return null
    return {
      ...m,
      body_text: m.body_preview || null,
      body_html: null,
      body_translated: m.body_translated_preview,
      to_addrs: [],
      cc_addrs: [],
    }
  }, [listRows, messageId])

  const selectedMsg = msgQ.data ?? listShell
  const bodyPending = Boolean(messageId && !msgQ.data && (msgQ.isLoading || msgQ.isFetching))
  const selectedAcct = selectedMsg ? labelById.get(selectedMsg.account_id) : null

  function prefetchMessage(id: string) {
    void queryClient.prefetchQuery({
      queryKey: ['message', id],
      queryFn: () => getMessage(id),
      staleTime: 60_000,
    })
  }

  const effectiveDrill: DrillView = messageId ? 'reader' : drillView

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params)
    if (value == null || value === '') next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  function patchParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k)
      else next.set(k, v)
    }
    setParams(next, { replace: true })
  }

  function setOffset(n: number) {
    setParam('offset', n === 0 ? null : String(n))
  }

  function setSec(next: SecId, extra?: Record<string, string | null>) {
    patchParams({
      sec: next === 'mail' ? null : next,
      agent: null,
      acct: null,
      ...(extra || {}),
    })
  }

  function navigateMailQuery(next: URLSearchParams, drill: DrillView) {
    next.delete('sec')
    next.delete('agent')
    next.delete('acct')
    next.delete('offset')
    setDrillView(drill)
    const qs = next.toString()
    // C1: leaving /inbox/:messageId is required — setSearchParams alone keeps the reader open.
    if (messageId) {
      navigate(`/inbox${qs ? `?${qs}` : ''}`)
    } else {
      setParams(next, { replace: true })
    }
  }

  function selectAccount(id: string, opts?: { drill?: DrillView }) {
    const next = new URLSearchParams(params)
    if (!id) next.delete('account')
    else next.set('account', id)
    next.set('folder', 'INBOX')
    // Drop sticky search when switching accounts — otherwise refresh looks like an empty inbox
    next.delete('q')
    setSearchLocal('')
    navigateMailQuery(next, opts?.drill ?? 'list')
  }

  function clearSearch() {
    setSearchLocal('')
    patchParams({ q: null, offset: null })
  }

  async function removeLinkedAccount(id: string) {
    const acct = labelById.get(id)
    const label = acct ? resolveLabel(acct) : id
    const ok = window.confirm(t('mail.deleteAccountConfirm', { label }))
    if (!ok) return
    try {
      await deleteAccount(id)
      await queryClient.invalidateQueries({ queryKey: ['accounts'] })
      await queryClient.invalidateQueries({ queryKey: ['messages-v3'] })
      patchParams({
        sec: null,
        acct: null,
        account: accountId === id ? null : accountId || null,
      })
      setDrillView('list')
    } catch (err) {
      window.alert(err instanceof Error ? err.message : t('mail.deleteAccountFailed'))
    }
  }

  function openFolder(id: FolderId, forAccount?: string) {
    const next = new URLSearchParams(params)
    next.set('folder', id)
    next.delete('notes')
    if (forAccount === undefined) {
      /* keep account */
    } else if (!forAccount) {
      next.delete('account')
    } else {
      next.set('account', forAccount)
    }
    navigateMailQuery(next, 'list')
    setNavShow(false)
  }

  function openMessage(id: string) {
    if (id !== messageId && !noteOpenIntentRef.current) setNoteEditorOpen(false)
    const qs = params.toString()
    setDrillView('reader')
    navigate(`/inbox/${id}${qs ? `?${qs}` : ''}`)
  }

  function closeReader() {
    noteOpenIntentRef.current = false
    setNoteEditorOpen(false)
    const qs = params.toString()
    setDrillView('list')
    navigate(`/inbox${qs ? `?${qs}` : ''}`)
  }

  function backFromList() {
    setDrillView('home')
  }

  async function toggleStar(id: string, starred: boolean) {
    const patchFlags = (old: unknown) => {
      if (!old || typeof old !== 'object') return old
      const o = old as { items?: MessageSummary[]; flags?: MessageSummary['flags']; id?: string }
      if (Array.isArray(o.items)) {
        return {
          ...o,
          items: o.items.map((m) =>
            m.id === id ? { ...m, flags: { ...m.flags, starred } } : m,
          ),
        }
      }
      if (o.id === id) {
        return { ...o, flags: { ...o.flags, starred } }
      }
      return old
    }
    queryClient.setQueriesData({ queryKey: ['messages-v3'] }, patchFlags)
    queryClient.setQueryData(['message', id], patchFlags)
    try {
      const res = await setMessageFlags(id, { starred })
      queryClient.setQueriesData({ queryKey: ['messages-v3'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        const o = old as { items?: MessageSummary[] }
        if (!Array.isArray(o.items)) return old
        return {
          ...o,
          items: o.items.map((m) =>
            m.id === id ? { ...m, flags: { ...m.flags, ...(res.flags || { starred }) } } : m,
          ),
        }
      })
      queryClient.setQueryData(['message', id], (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        const o = old as MessageSummary
        return { ...o, flags: { ...o.flags, ...(res.flags || { starred }) } }
      })
    } catch {
      void messagesQ.refetch()
      void msgQ.refetch()
    }
  }

  async function saveNoticeText(id: string, text: string) {
    const trimmed = text.trim()
    setNoteBusy(true)
    try {
      const res = await setMessageNote(id, trimmed || null)
      queryClient.setQueriesData({ queryKey: ['messages-v3'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        const o = old as { items?: MessageSummary[] }
        if (!Array.isArray(o.items)) return old
        return {
          ...o,
          items: o.items.map((m) =>
            m.id === id ? { ...m, has_note: res.has_note } : m,
          ),
        }
      })
      queryClient.setQueryData(['message', id], (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        return {
          ...(old as MessageDetail),
          has_note: res.has_note,
          user_note: res.user_note,
        }
      })
      setNoteDraft(res.user_note || '')
      setNoteEditorOpen(false)
    } catch {
      void msgQ.refetch()
    } finally {
      setNoteBusy(false)
    }
  }

  function openNoticeEditor(id: string, existing?: string | null) {
    const draft =
      existing && existing !== '•' ? existing : existing === '•' ? '' : existing === null ? '' : undefined
    if (id === messageId) {
      if (draft !== undefined) setNoteDraft(draft)
      setNoteEditorOpen(true)
      return
    }
    noteOpenIntentRef.current = true
    openMessage(id)
    if (draft !== undefined) setNoteDraft(draft)
    setNoteEditorOpen(true)
  }

  function toggleAcc(id: string) {
    setOpenAcc((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  async function runAgentAsk(override?: { question?: string; scope?: string }) {
    const question = (override?.question ?? agentQ).trim()
    const scope = override?.scope ?? agentScope
    if (!question || agentBusy) return
    setAgentBusy(true)
    try {
      const res = await agentAsk({
        question,
        message_id: scope === 'connect_help' ? undefined : messageId,
        scope: scope || undefined,
      })
      setAgentAns(res.answer)
      setAgentSources(res.sources || [])
      setAgentMeta({
        why: res.why,
        privacy: res.privacy,
        location: res.privacy_location,
      })
    } catch (err) {
      setAgentAns(err instanceof Error ? err.message : 'error')
      setAgentSources([])
      setAgentMeta(null)
    } finally {
      setAgentBusy(false)
    }
  }

  function openAgent(opts?: { from?: SecId; scope?: string; question?: string; autoAsk?: boolean }) {
    const from = opts?.from
    if (from) setAgentReturnTo(from)
    else setAgentReturnTo(null)
    if (opts?.scope) setAgentScope(opts.scope)
    if (opts?.question != null) setAgentQ(opts.question)
    setSec('agent')
    if (opts?.autoAsk && opts.question) {
      void runAgentAsk({ question: opts.question, scope: opts.scope })
    }
  }

  function closeAgent() {
    const back = agentReturnTo || 'mail'
    setAgentReturnTo(null)
    setAgentScope(undefined)
    setSec(back)
  }

  function toggleAgent() {
    if (sec === 'agent') {
      closeAgent()
      return
    }
    openAgent({ from: sec === 'add' ? 'add' : undefined })
  }

  function openConnectHelp() {
    const qHelp = t('mail.connectHelpQuestion')
    setAgentAns(null)
    setAgentSources([])
    setAgentMeta(null)
    openAgent({ from: 'add', scope: 'connect_help', question: qHelp, autoAsk: true })
  }

  const originalHtml = useMemo(() => {
    if (!selectedMsg) return ''
    if (selectedMsg.body_html) return sanitizeEmailHtml(selectedMsg.body_html, { blockRemote: true })
    return textToSafeHtml(selectedMsg.body_text || '')
    // Depend on body fields only — flag/note patches must NOT rebuild HTML (aborts image hydrate).
  }, [selectedMsg?.id, selectedMsg?.body_html, selectedMsg?.body_text])

  const goodTranslation = useMemo(() => {
    if (!selectedMsg?.body_translated) return null
    if (
      translationLooksGarbage(
        selectedMsg.body_text || selectedMsg.body_html || '',
        selectedMsg.body_translated,
      )
    ) {
      return null
    }
    return selectedMsg.body_translated
  }, [selectedMsg?.id, selectedMsg?.body_translated, selectedMsg?.body_text, selectedMsg?.body_html])

  const translatedHtml = useMemo(() => {
    if (!goodTranslation) return ''
    return textToSafeHtml(goodTranslation)
  }, [goodTranslation])

  const blockedRemoteCount = useMemo(() => countBlockedRemote(originalHtml), [originalHtml])
  const accountImageMode: ImageDisplayMode =
    (selectedAcct?.image_display_mode as ImageDisplayMode | undefined) || 'ask'
  const senderAlways = isSenderImagesAlwaysAllowed(selectedMsg?.from_addr)
  const senderSession = isSenderImagesSessionAllowed(selectedMsg?.from_addr)
  const messageSession = isMessageImagesSessionAllowed(messageId)
  // Roof: never blocks all; always auto-loads; ask uses per-msg/sender prefs (unchanged).
  const loadRemote =
    accountImageMode === 'always'
      ? true
      : accountImageMode === 'never'
        ? false
        : loadRemoteOnce || senderAlways || senderSession || messageSession
  const showImageBanner =
    blockedRemoteCount > 0 && accountImageMode !== 'always' && !loadRemote
  const attachmentMeta = selectedMsg?.attachment_names

  // Stabilize attachment list for iframe hydrate
  const attachmentKey = useMemo(
    () =>
      JSON.stringify(
        (attachmentMeta || []).map((a) => [a.content_id || '', Boolean(a.saved)]),
      ),
    [attachmentMeta],
  )
  const attachmentsStable = useMemo(
    () => attachmentMeta || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attachmentKey],
  )

  useEffect(() => {
    if (!messageId) {
      setLoadRemoteOnce(false)
      return
    }
    if (accountImageMode === 'always') {
      setLoadRemoteOnce(true)
      return
    }
    if (accountImageMode === 'never') {
      setLoadRemoteOnce(false)
      return
    }
    // Restore "load once" / session allow after F5 for this message
    setLoadRemoteOnce(
      isMessageImagesSessionAllowed(messageId) ||
        isSenderImagesSessionAllowed(selectedMsg?.from_addr) ||
        isSenderImagesAlwaysAllowed(selectedMsg?.from_addr),
    )
  }, [messageId, selectedMsg?.from_addr, accountImageMode])

  useEffect(() => {
    if (messageId) setDrillView('reader')
  }, [messageId])

  // D1: opening a message marks it read on the server and refreshes list/accounts
  useEffect(() => {
    if (!messageId || !msgQ.data) return
    if (msgQ.data.flags?.read !== false) return
    let cancelled = false
    void (async () => {
      try {
        const res = await setMessageFlags(messageId, { read: true })
        if (cancelled) return
        // Optimistic list patch — invalidate key must match messages-v3 (not 'messages')
        queryClient.setQueriesData({ queryKey: ['messages-v3'] }, (old: unknown) => {
          if (!old || typeof old !== 'object') return old
          const o = old as { items?: MessageSummary[] }
          if (!Array.isArray(o.items)) return old
          return {
            ...o,
            items: o.items.map((m) =>
              m.id === messageId
                ? { ...m, flags: { ...m.flags, ...(res.flags || { read: true }) } }
                : m,
            ),
          }
        })
        queryClient.setQueryData(['message', messageId], (old: unknown) => {
          if (!old || typeof old !== 'object') return old
          const o = old as MessageSummary
          return { ...o, flags: { ...o.flags, ...(res.flags || { read: true }) } }
        })
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['messages-v3'] }),
          queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        ])
      } catch {
        /* non-fatal — list may stay unread until next sync */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [messageId, msgQ.data, queryClient])

  // Migrate legacy view=split → both; agent=1 → sec=agent
  useEffect(() => {
    const rawView = params.get('view')
    const legacyAgent = params.get('agent') === '1'
    if (rawView !== 'split' && !legacyAgent) return
    const next = new URLSearchParams(params)
    if (rawView === 'split') next.set('view', 'both')
    if (legacyAgent) {
      next.set('sec', 'agent')
      next.delete('agent')
    }
    setParams(next, { replace: true })
  }, [params, setParams])

  useEffect(() => {
    if (noteOpenIntentRef.current) {
      noteOpenIntentRef.current = false
      setNoteEditorOpen(true)
      return
    }
    setNoteEditorOpen(false)
  }, [messageId])

  useEffect(() => {
    if (!noteEditorOpen || !selectedMsg) return
    const n = selectedMsg.user_note || ''
    setNoteDraft(n === '•' ? '' : n)
  }, [noteEditorOpen, selectedMsg?.id, selectedMsg?.user_note])

  useEffect(() => {
    if (!undoBar) return
    const iv = setInterval(() => setUndoTick((n) => n + 1), 1000)
    return () => clearInterval(iv)
  }, [undoBar])

  useEffect(() => {
    if (undoBar && Date.now() >= undoBar.until) setUndoBar(null)
  }, [undoBar, undoTick])

  useEffect(() => {
    if (!undoBar) return
    let cancelled = false
    const poll = async () => {
      try {
        const st = await getSendStatus(undoBar.id)
        if (cancelled) return
        if (['sent', 'failed', 'cancelled'].includes(st.status)) {
          setUndoBar(null)
        }
      } catch {
        /* ignore transient */
      }
    }
    void poll()
    const iv = setInterval(() => void poll(), 1500)
    return () => {
      cancelled = true
      clearInterval(iv)
    }
  }, [undoBar])

  const undoSecondsLeft = undoBar ? Math.max(0, Math.ceil((undoBar.until - Date.now()) / 1000)) : 0
  const effectiveView: ViewMode = goodTranslation ? view : 'original'
  const acctEditAccount = acctId ? labelById.get(acctId) : null
  const acctEditIdx = acctEditAccount ? accounts.findIndex((a) => a.id === acctEditAccount.id) : 0

  const folderLabel = t(FOLDERS.find((f) => f.id === folder)?.labelKey || 'mail.folderInbox')
  const listSub = activeAccount ? resolveLabel(activeAccount) : t('mail.allAccounts')
  const listTotal = messagesQ.data?.total ?? messagesQ.data?.count ?? listRows.length
  const pageVisible = listRows.length
  const pageFrom = listTotal === 0 ? 0 : offset + 1
  const pageTo = listTotal === 0 ? 0 : offset + pageVisible

  function renderFolderButtons(forAccountId: string) {
    const inboxTotal = messagesQ.data?.total
    const starredCount = (messagesQ.data?.items || []).filter((m) => m.flags?.starred).length
    const draftCount = draftsQ.data?.count || 0
    return (
      <>
        {FOLDERS.map((f) => {
          const selected =
            folder === f.id &&
            (forAccountId === 'all' ? !accountId : accountId === forAccountId)
          return (
            <button
              key={`${forAccountId}-${f.id}`}
              type="button"
              className={`rd-fold${selected ? ' sel' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                openFolder(f.id, forAccountId === 'all' ? '' : forAccountId)
              }}
            >
              <Icon name={f.icon} size={17} />
              {t(f.labelKey)}
              {f.id === 'INBOX' && inboxTotal != null && (forAccountId === 'all' || forAccountId === accountId) ? (
                <span className="c">{inboxTotal}</span>
              ) : null}
              {f.id === 'STARRED' && starredCount > 0 ? <span className="c">{starredCount}</span> : null}
              {f.id === 'DRAFT' && draftCount > 0 ? <span className="c">{draftCount}</span> : null}
            </button>
          )
        })}
        <button type="button" className="rd-fold new" disabled title={t('mail.soon')}>
          <Icon name="folder" size={17} />
          {t('mail.newFolder')}
        </button>
      </>
    )
  }

  function openAccountCustomize(id: string) {
    setSec('acct', { acct: id })
  }

  const mailPanelsActive = sec === 'mail'

  return (
    <div
      className="mail-app mail-rd"
      data-section={sec}
      data-view={effectiveDrill}
      data-nav={navShow ? 'open' : 'closed'}
    >
      {undoBar ? (
        <div className="rd-undo" role="status">
          <span>
            {t('compose.queuedUndo')} ({undoSecondsLeft}s)
          </span>
          <button
            type="button"
            className="mail-send"
            onClick={() => {
              void undoSend(undoBar.id)
                .then(() => setUndoBar(null))
                .catch(() => setUndoBar(null))
            }}
          >
            {t('compose.undo')}
          </button>
        </div>
      ) : null}
      {oauthBanner ? (
        <div
          className={`rd-oauth-banner${oauthBanner.kind === 'warn' ? ' warn' : ' ok'}`}
          role={oauthBanner.kind === 'warn' ? 'alert' : 'status'}
        >
          <span>{oauthBanner.text}</span>
          <button type="button" className="rd-rbtn2" onClick={() => setOauthBanner(null)}>
            OK
          </button>
        </div>
      ) : null}

      <ComposeModal
        open={Boolean(compose)}
        seed={compose}
        onClose={() => setCompose(null)}
        onSent={() => {
          void messagesQ.refetch()
          void draftsQ.refetch()
        }}
        onQueued={(info) => setUndoBar({ id: info.id, until: info.undoUntil })}
        onDraftSaved={() => {
          void draftsQ.refetch()
        }}
      />

      {/* Activity rail: logo · accounts (scroll) · + / settings (+ desktop mail/cal/contacts) */}
      <nav className="rd-rail" aria-label={t('mail.accounts')}>
        <div className="rd-rail-top">
          <Link to="/welcome" className="rd-logo" aria-label="7GC Mail">
            <img src="/email-7.png" alt="" width={34} height={34} />
          </Link>

          {/* All-mail — always visible under logo (not rd-rail-desk; was hidden/overlapped on some viewports) */}
          <div className="rd-rwrap">
            <button
              type="button"
              className={`rd-rbtn${sec === 'mail' && !accountId ? ' active' : ''}`}
              title={t('mail.mailNav')}
              aria-label={t('mail.mailNav')}
              onClick={() => {
                // Same as bottom "Lista": all-accounts inbox list — not folder drawer (hamburger does that)
                setSec('mail')
                selectAccount('', { drill: 'list' })
                setNavShow(false)
              }}
            >
              <Icon name="mail" size={22} />
              {totalUnread > 0 ? <span className="badge">{totalUnread > 99 ? '99+' : totalUnread}</span> : null}
            </button>
            <span className="rd-rlabel rd-rail-desk">{t('mail.mailNav')}</span>
          </div>

          <div className="rd-sep" />
        </div>

        <div className="rd-rail-mid">
          {accounts.map((a, idx) => {
            const unread = a.unread_count || 0
            return (
              <button
                key={a.id}
                type="button"
                className={`rd-rbtn rd-rbtn-acct${sec === 'mail' && accountId === a.id ? ' active' : ''}`}
                style={{ ['--acct-accent' as string]: resolveColor(a, idx) }}
                title={resolveLabel(a)}
                onClick={() => {
                  selectAccount(a.id)
                }}
                onContextMenu={(e) => {
                  e.preventDefault()
                  openAccountCustomize(a.id)
                }}
                onTouchStart={(e) => {
                  const touch = e.touches[0]
                  if (!touch) return
                  if (pressTimerRef.current) clearTimeout(pressTimerRef.current)
                  pressTimerRef.current = setTimeout(() => openAccountCustomize(a.id), 550)
                }}
                onTouchEnd={() => {
                  if (pressTimerRef.current) clearTimeout(pressTimerRef.current)
                }}
                onTouchMove={() => {
                  if (pressTimerRef.current) clearTimeout(pressTimerRef.current)
                }}
              >
                <AccountAvatar
                  account={a}
                  color={resolveColor(a, idx)}
                  initial={resolveInitial(a)}
                  size={30}
                  revision={acctAvatarRevision}
                />
                {unread > 0 ? <span className="badge">{unread > 99 ? '99+' : unread}</span> : null}
              </button>
            )
          })}
        </div>

        <div className="rd-rail-bot">
          <div className="rd-sep" />
          <div className="rd-rwrap rd-rail-desk">
            <button
              type="button"
              className={`rd-rbtn${sec === 'calendar' ? ' active' : ''}`}
              title={t('mail.calendar')}
              onClick={() => setSec('calendar')}
            >
              <Icon name="calendar_month" size={22} />
            </button>
            <span className="rd-rlabel">{t('mail.calendar')}</span>
          </div>
          <div className="rd-rwrap rd-rail-desk">
            <button
              type="button"
              className={`rd-rbtn${sec === 'contacts' ? ' active' : ''}`}
              title={t('mail.contacts')}
              onClick={() => setSec('contacts')}
            >
              <Icon name="contacts" size={22} />
            </button>
            <span className="rd-rlabel">{t('mail.contacts')}</span>
          </div>
          <div className="rd-rwrap rd-rail-desk">
            <button
              type="button"
              className={`rd-rbtn${sec === 'agent' ? ' active' : ''}${sec === 'add' ? ' hint' : ''}`}
              title={t('mail.agentTitle')}
              onClick={() => toggleAgent()}
            >
              <Icon name="auto_awesome" size={22} />
            </button>
            <span className="rd-rlabel">Agent</span>
          </div>
          <div className="rd-rwrap">
            <button
              type="button"
              className={`rd-rbtn${sec === 'add' ? ' active' : ''}`}
              title={t('mail.addAccount')}
              onClick={() => setSec('add')}
            >
              <Icon name="add" size={22} />
            </button>
            <span className="rd-rlabel">{t('mail.addAccount').split(' ')[0]}</span>
          </div>
          <div className="rd-rwrap rd-user-wrap">
            <button
              ref={userMenuBtnRef}
              type="button"
              className={`rd-rbtn rd-user-btn${userMenuOpen || sec === 'profile' ? ' active' : ''}`}
              title={t('profile.menuAria')}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              onClick={() => setUserMenuOpen((v) => !v)}
            >
              <UserAvatar size={30} revision={avatarRevision} />
            </button>
            <span className="rd-rlabel">{railDisplayName}</span>
            <UserMenu
              open={userMenuOpen}
              onClose={() => setUserMenuOpen(false)}
              avatarRevision={avatarRevision}
              anchorRef={userMenuBtnRef}
              onOpenProfile={() => setSec('profile')}
              onOpenSettings={() => setSec('settings')}
              onOpenAccount={(id) => openAccountCustomize(id)}
            />
          </div>
        </div>
      </nav>

      <div className="rd-ws">
        <div className="rd-ws-body">
        {/* Accordion nav */}
        <aside
          ref={navRef}
          className={`rd-panel rd-nav${navShow ? ' show' : ''}${mailPanelsActive && effectiveDrill === 'home' ? ' active' : ''}`}
          data-panel="home"
          aria-hidden={navShow || (mailPanelsActive && effectiveDrill === 'home') ? undefined : true}
        >
          <div className="rd-panel-head">
            <button
              type="button"
              className="rd-back forNav always"
              aria-label={t('mail.navClose')}
              onClick={() => setNavShow(false)}
            >
              <Icon name="close" size={20} />
            </button>
            <div>
              <h2>{t('mail.allAccounts')}</h2>
              <span className="sub">{t('mail.linkedCount', { count: accounts.length })}</span>
            </div>
          </div>
          <button
            type="button"
            className="rd-compose"
            onClick={() =>
              setCompose({
                mode: 'new',
                accountId: accountId || accounts[0]?.id,
              })
            }
          >
            <Icon name="edit" size={18} />
            {t('mail.compose')}
          </button>
          <div className="rd-scroll">
            <div className={`rd-acc${openAcc.all ? ' open' : ''}`}>
              <button type="button" className="rd-acc-head" onClick={() => toggleAcc('all')}>
                <span
                  className="rd-dot"
                  style={{ background: 'linear-gradient(135deg, #7c6cff, #a99bff)' }}
                >
                  Σ
                </span>
                <div className="rd-acc-meta">
                  <div className="n">{t('mail.allAccounts')}</div>
                  <div className="e">{t('mail.unifiedView')}</div>
                </div>
                {totalUnread > 0 ? <span className="rd-unread">{totalUnread}</span> : null}
                <Icon name="expand_more" size={16} className="rd-chev" />
              </button>
              <div className="rd-folders">{renderFolderButtons('all')}</div>
            </div>

            {accounts.map((a, idx) => {
              const isOpen = Boolean(openAcc[a.id])
              return (
                <div key={a.id} className={`rd-acc${isOpen ? ' open' : ''}`}>
                  <button
                    type="button"
                    className="rd-acc-head"
                    onClick={() => toggleAcc(a.id)}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      openAccountCustomize(a.id)
                    }}
                  >
                    <AccountAvatar
                      account={a}
                      color={resolveColor(a, idx)}
                      initial={resolveInitial(a)}
                      size={30}
                      revision={acctAvatarRevision}
                    />
                    <div className="rd-acc-meta">
                      <div className="n">{resolveLabel(a)}</div>
                      <div className="e">{a.username}</div>
                      {a.last_sync_status && a.last_sync_status !== 'ok' ? (
                        <div className="e rd-sync-err" title={a.last_sync_error || a.last_sync_status}>
                          {a.last_sync_error || a.last_sync_status}
                          {(a.total_all || 0) === 0 ? ' · 0 msgs' : ''}
                        </div>
                      ) : null}
                      {a.last_sync_status === 'ok' && (a.total_all || 0) === 0 ? (
                        <div className="e rd-sync-warn">{t('mail.syncEmpty')}</div>
                      ) : null}
                      {(a.last_sync_status && a.last_sync_status !== 'ok') ||
                      (a.last_sync_error || '').includes('scope') ||
                      (a.last_sync_error || '').includes('PERMISSION') ||
                      (a.last_sync_error || '').includes('403') ? (
                        <button
                          type="button"
                          className="rd-acc-reconnect"
                          onClick={(e) => {
                            e.stopPropagation()
                            const provider =
                              a.provider === 'microsoft' || a.provider === 'gmail'
                                ? a.provider
                                : a.provider?.includes('microsoft')
                                  ? 'microsoft'
                                  : 'gmail'
                            void oauthStart(provider as 'gmail' | 'microsoft')
                              .then((r) => {
                                window.location.assign(r.authorize_url)
                              })
                              .catch(() => {
                                /* ignore */
                              })
                          }}
                        >
                          {t('mail.reconnectOAuth')}
                        </button>
                      ) : null}
                    </div>
                    {(a.unread_count || 0) > 0 ? (
                      <span className="rd-unread">{a.unread_count}</span>
                    ) : null}
                    <Icon name="expand_more" size={16} className="rd-chev" />
                  </button>
                  <div className="rd-folders">{renderFolderButtons(a.id)}</div>
                </div>
              )
            })}
          </div>
        </aside>

        {/* List */}
        <section
          className={`rd-panel rd-list${mailPanelsActive && effectiveDrill === 'list' ? ' active' : ''}`}
          data-panel="list"
        >
          <div className="rd-panel-head">
            <button
              type="button"
              className="rd-back"
              aria-label={t('auth.cancel')}
              onClick={backFromList}
            >
              <Icon name="arrow_back" size={20} />
            </button>
            <button
              type="button"
              className="rd-back forNav"
              aria-label={navShow ? t('mail.navClose') : t('mail.navOpen')}
              aria-pressed={navShow}
              onClick={() => setNavShow((v) => !v)}
            >
              <Icon name="menu" size={20} />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2>{folderLabel}</h2>
              <span className="sub">{listSub}</span>
            </div>
            {listTotal > 0 ? (
              <span
                className="rd-list-count"
                title={
                  accountId
                    ? t('mail.headerCountAccount', { count: listTotal })
                    : t('mail.headerCountAll', { count: listTotal })
                }
              >
                {t('mail.headerCount', { count: listTotal.toLocaleString() })}
              </span>
            ) : null}
            <div className="rd-list-lang">
              <LanguageMenu />
            </div>
          </div>
          <div className="rd-search-wrap">
            <input
              className="rd-search rd-list-search"
              value={searchLocal}
              onChange={(e) => setSearchLocal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  patchParams({ q: searchLocal.trim() || null, offset: null })
                }
              }}
              placeholder={t('mail.searchPlaceholder')}
              aria-label={t('mail.searchPlaceholder')}
            />
            {searchLocal || q ? (
              <button
                type="button"
                className="rd-search-clear"
                title={t('mail.clearSearch')}
                aria-label={t('mail.clearSearch')}
                onClick={clearSearch}
              >
                <Icon name="close" size={18} />
              </button>
            ) : null}
          </div>
          <div className="rd-tabs rd-list-tools" role="toolbar" aria-label={t('mail.folders')}>
            {LIST_TOOL_FOLDERS.map((id) => {
              const meta = FOLDERS.find((f) => f.id === id)!
              const selected = folder === id
              return (
                <button
                  key={id}
                  type="button"
                  className={`rd-tab rd-tool${selected ? ' sel' : ''}`}
                  title={t(meta.labelKey)}
                  aria-label={t(meta.labelKey)}
                  aria-pressed={selected}
                  onClick={() => openFolder(id)}
                >
                  <Icon
                    name={meta.icon}
                    size={20}
                    style={
                      id === 'STARRED' && selected
                        ? { fontVariationSettings: "'FILL' 1" }
                        : undefined
                    }
                  />
                </button>
              )
            })}
            <button
              type="button"
              className={`rd-tab rd-tool${notesOnly ? ' sel' : ''}`}
              title={t('mail.notesFilter')}
              aria-label={t('mail.notesFilter')}
              aria-pressed={notesOnly}
              onClick={() => {
                if (notesOnly) {
                  patchParams({ notes: null, offset: null })
                } else {
                  patchParams({ notes: '1', offset: null, folder: 'INBOX' })
                }
              }}
            >
              <Icon
                name="sticky_note_2"
                size={20}
                style={notesOnly ? { fontVariationSettings: "'FILL' 1", color: '#dc2626' } : undefined}
              />
            </button>
            <button
              type="button"
              className="rd-tab rd-tool rd-tool-compose"
              title={t('mail.compose')}
              aria-label={t('mail.compose')}
              onClick={() =>
                setCompose({
                  mode: 'new',
                  accountId: accountId || accounts[0]?.id,
                })
              }
            >
              <Icon name="edit" size={18} />
              <span className="rd-tool-label">{t('mail.compose')}</span>
            </button>
          </div>
          <div className="rd-scroll">
            {folder === 'DRAFT' ? (
              <>
                {draftsQ.isLoading ? <p className="mail-empty-reader">{t('inbox.loading')}</p> : null}
                {draftsQ.isError ? (
                  <p className="mail-empty-reader" role="alert">
                    {t('inbox.loadFailed')}
                  </p>
                ) : null}
                {draftItems.map((d) => {
                  const acct = d.account_id ? labelById.get(d.account_id) : undefined
                  const idx = acct ? accounts.findIndex((a) => a.id === acct.id) : 0
                  const preview = (d.body_text || '').replace(/\s+/g, ' ').trim().slice(0, 120)
                  return (
                    <button
                      key={d.id}
                      type="button"
                      className="rd-msg"
                      style={{
                        ['--acct-accent' as string]: acct
                          ? resolveColor(acct, Math.max(0, idx))
                          : 'var(--m-primary)',
                      }}
                      onClick={() =>
                        setCompose({
                          mode: 'new',
                          draftId: d.id,
                          accountId: d.account_id || undefined,
                        })
                      }
                    >
                      <div className="body">
                        <div className="row1">
                          <span className="from">{d.to_addrs || t('compose.needTo')}</span>
                          <span className="time">{formatWhen(d.updated_at)}</span>
                        </div>
                        <div className="subj">{d.subject || '(no subject)'}</div>
                        <div className="prev">{preview}</div>
                      </div>
                    </button>
                  )
                })}
                {!draftsQ.isLoading && draftItems.length === 0 ? (
                  <p className="mail-empty-reader">{t('inbox.empty')}</p>
                ) : null}
              </>
            ) : (
              <>
                {messagesQ.isLoading ? <p className="mail-empty-reader">{t('inbox.loading')}</p> : null}
                {messagesQ.isError ? (
                  <p className="mail-empty-reader" role="alert">
                    {t('inbox.loadFailed')}
                  </p>
                ) : null}
                {listRows.map((m) => {
                  const acct = labelById.get(m.account_id)
                  const unread = m.flags?.read === false
                  const starred = Boolean(m.flags?.starred)
                  const noticed = Boolean(m.has_note)
                  const chip = langChip(m)
                  const idx = acct ? accounts.findIndex((a) => a.id === acct.id) : 0
                  return (
                    <div
                      key={m.id}
                      className={`rd-msg${messageId === m.id ? ' sel' : ''}${unread ? '' : ' read'}${starred ? ' starred' : ''}${noticed ? ' noticed' : ''}`}
                      style={{
                        ['--acct-accent' as string]: acct
                          ? resolveColor(acct, Math.max(0, idx))
                          : 'var(--m-primary)',
                      }}
                      onMouseEnter={() => prefetchMessage(m.id)}
                      onFocus={() => prefetchMessage(m.id)}
                    >
                      <button
                        type="button"
                        className="rd-msg-main"
                        onClick={() => openMessage(m.id)}
                      >
                        <div className="body">
                          <div className="row1">
                            <span className="from">
                              {m.from_name ? `${m.from_name} <${m.from_addr || ''}>` : m.from_addr || '—'}
                            </span>
                            <span className="time">{formatWhen(m.date_sent)}</span>
                          </div>
                          <div className="subj">
                            {m.subject || '(no subject)'}
                            {m.threadCount > 1 ? ` · ${m.threadCount}` : ''}
                          </div>
                          <div className="prev">{m.body_preview || m.body_translated_preview || ''}</div>
                          {chip ? <span className="rd-langtag">{chip}</span> : null}
                        </div>
                      </button>
                      <button
                        type="button"
                        className={`rd-msg-notice${noticed ? ' on' : ''}`}
                        aria-label={noticed ? t('mail.noticeEdit') : t('mail.noticeAdd')}
                        aria-pressed={noticed}
                        title={noticed ? t('mail.noticeEdit') : t('mail.noticeAdd')}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          openNoticeEditor(m.id)
                        }}
                      >
                        <Icon
                          name="sticky_note_2"
                          size={18}
                          style={noticed ? { fontVariationSettings: "'FILL' 1" } : undefined}
                        />
                      </button>
                      <button
                        type="button"
                        className={`rd-msg-star${starred ? ' on' : ''}`}
                        aria-label={starred ? t('mail.starRemove') : t('mail.starAdd')}
                        aria-pressed={starred}
                        title={starred ? t('mail.starRemove') : t('mail.starAdd')}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          void toggleStar(m.id, !starred)
                        }}
                      >
                        <Icon
                          name="star"
                          size={18}
                          style={starred ? { fontVariationSettings: "'FILL' 1" } : undefined}
                        />
                      </button>
                    </div>
                  )
                })}
                {!messagesQ.isLoading && listRows.length === 0 ? (
                  q ? (
                    <div className="mail-empty-reader rd-empty-search">
                      <p>{t('inbox.emptySearch', { q })}</p>
                      <button type="button" className="rd-tab rd-tool-compose" onClick={clearSearch}>
                        {t('mail.clearSearch')}
                      </button>
                    </div>
                  ) : (
                    <p className="mail-empty-reader">{t('inbox.empty')}</p>
                  )
                ) : null}
              </>
            )}
          </div>
          {folder !== 'DRAFT' ? (
            <div className="rd-pager">
              <button
                type="button"
                className="rd-pager-nav"
                disabled={offset <= 0}
                aria-label={t('mail.pagePrev')}
                title={t('mail.pagePrev')}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                «
              </button>
              <span className="meta">
                {t('mail.pageRange', {
                  from: pageFrom,
                  to: pageTo,
                  total: listTotal,
                })}
              </span>
              <button
                type="button"
                className="rd-pager-nav"
                disabled={offset + (messagesQ.data?.limit ?? PAGE_SIZE) >= listTotal || pageVisible === 0}
                aria-label={t('mail.pageNext')}
                title={t('mail.pageNext')}
                onClick={() => setOffset(offset + (messagesQ.data?.limit ?? PAGE_SIZE))}
              >
                »
              </button>
            </div>
          ) : null}
        </section>

        {/* Reader */}
        <section
          className={`rd-panel rd-read${mailPanelsActive && effectiveDrill === 'reader' ? ' active' : ''}`}
          data-panel="reader"
          aria-live="polite"
        >
          {!messageId ? (
            <div className="mail-empty-reader">{t('mail.pickMessage')}</div>
          ) : msgQ.isError && !selectedMsg ? (
            <div className="mail-empty-reader">{t('reader.loadFailed')}</div>
          ) : !selectedMsg ? (
            <div className="mail-empty-reader">{t('inbox.loading')}</div>
          ) : (
            <>
              <div className="rd-panel-head">
                <button
                  type="button"
                  className="rd-back"
                  aria-label={t('reader.backInbox')}
                  onClick={closeReader}
                >
                  <Icon name="arrow_back" size={20} />
                </button>
                <button
                  type="button"
                  className="rd-back forNav"
                  aria-label={navShow ? t('mail.navClose') : t('mail.navOpen')}
                  aria-pressed={navShow}
                  onClick={() => setNavShow((v) => !v)}
                >
                  <Icon name="menu" size={20} />
                </button>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h2 style={{ fontSize: 14 }}>
                    {selectedMsg.from_name || selectedMsg.from_addr || '—'}
                  </h2>
                  <span className="sub">
                    {selectedAcct ? resolveLabel(selectedAcct) : ''}
                    {selectedMsg.date_sent
                      ? ` · ${formatWhen(selectedMsg.date_sent)}`
                      : ''}
                  </span>
                </div>
                <button
                  type="button"
                  className={`rd-msg-notice rd-reader-notice${selectedMsg.has_note ? ' on' : ''}`}
                  aria-label={
                    selectedMsg.has_note ? t('mail.noticeEdit') : t('mail.noticeAdd')
                  }
                  aria-pressed={Boolean(selectedMsg.has_note)}
                  title={selectedMsg.has_note ? t('mail.noticeEdit') : t('mail.noticeAdd')}
                  onClick={() => {
                    setNoteDraft(
                      selectedMsg.user_note && selectedMsg.user_note !== '•'
                        ? selectedMsg.user_note
                        : '',
                    )
                    setNoteEditorOpen((v) => !v)
                  }}
                >
                  <Icon
                    name="sticky_note_2"
                    size={20}
                    style={
                      selectedMsg.has_note
                        ? { fontVariationSettings: "'FILL' 1" }
                        : undefined
                    }
                  />
                </button>
                <button
                  type="button"
                  className={`rd-msg-star rd-reader-star${selectedMsg.flags?.starred ? ' on' : ''}`}
                  aria-label={
                    selectedMsg.flags?.starred ? t('mail.starRemove') : t('mail.starAdd')
                  }
                  aria-pressed={Boolean(selectedMsg.flags?.starred)}
                  title={selectedMsg.flags?.starred ? t('mail.starRemove') : t('mail.starAdd')}
                  onClick={() => void toggleStar(selectedMsg.id, !selectedMsg.flags?.starred)}
                >
                  <Icon
                    name="star"
                    size={20}
                    style={
                      selectedMsg.flags?.starred
                        ? { fontVariationSettings: "'FILL' 1" }
                        : undefined
                    }
                  />
                </button>
              </div>
              <div className="rd-ractions">
                <div className="rd-ractions-row">
                  <button
                    type="button"
                    className="rd-rbtn2 ai"
                    title={t('mail.summarize')}
                    onClick={() => {
                      openAgent({
                        question: t('mail.summarizePrompt'),
                      })
                    }}
                  >
                    <Icon name="auto_awesome" size={16} /> {t('mail.summarize')}
                  </button>
                  <button
                    type="button"
                    className="rd-rbtn2"
                    onClick={() =>
                      setCompose({
                        mode: 'reply',
                        messageId: selectedMsg.id,
                        accountId: selectedMsg.account_id,
                      })
                    }
                  >
                    <Icon name="reply" size={16} /> {t('mail.reply')}
                  </button>
                  <div className="rd-ractions-wide" aria-hidden="true">
                    <button
                      type="button"
                      className="rd-rbtn2"
                      onClick={() =>
                        setCompose({
                          mode: 'replyAll',
                          messageId: selectedMsg.id,
                          accountId: selectedMsg.account_id,
                        })
                      }
                    >
                      <Icon name="reply" size={16} /> {t('mail.replyAll')}
                    </button>
                    <button
                      type="button"
                      className="rd-rbtn2"
                      onClick={() =>
                        setCompose({
                          mode: 'forward',
                          messageId: selectedMsg.id,
                          accountId: selectedMsg.account_id,
                        })
                      }
                    >
                      <Icon name="forward" size={16} /> {t('mail.forward')}
                    </button>
                    <button type="button" className="rd-rbtn2" disabled title={t('mail.soon')}>
                      <Icon name="delete" size={16} />
                    </button>
                    <button type="button" className="rd-rbtn2" disabled title={t('mail.soon')}>
                      <Icon name="schedule" size={16} />
                    </button>
                  </div>
                  <button
                    type="button"
                    className={`rd-rbtn2 rd-send-opts-toggle${sendOptsOpen ? ' open' : ''}`}
                    aria-expanded={sendOptsOpen}
                    onClick={() => setSendOptsOpen((v) => !v)}
                  >
                    {t('mail.sendOptions')} <Icon name="expand_more" size={16} />
                  </button>
                  <button
                    type="button"
                    className={`rd-rbtn2 rd-identity-btn${identityRisk ? ' warn' : ''}${identityOpen ? ' open' : ''}`}
                    aria-expanded={identityOpen}
                    aria-controls="rd-identity-panel"
                    onClick={() => setIdentityOpen((v) => !v)}
                  >
                    <Icon name={identityRisk ? 'warning' : 'shield'} size={16} />
                    {identityRisk ? t('mail.identityRisk') : t('mail.identity')}
                    <Icon name="expand_more" size={16} />
                  </button>
                </div>
                <div
                  className={`rd-send-opts-body${sendOptsOpen ? ' is-open' : ''}`}
                  aria-hidden={!sendOptsOpen}
                >
                    <button
                      type="button"
                      className="rd-rbtn2"
                      onClick={() =>
                        setCompose({
                          mode: 'replyAll',
                          messageId: selectedMsg.id,
                          accountId: selectedMsg.account_id,
                        })
                      }
                    >
                      <Icon name="reply" size={16} /> {t('mail.replyAll')}
                    </button>
                    <button
                      type="button"
                      className="rd-rbtn2"
                      onClick={() =>
                        setCompose({
                          mode: 'forward',
                          messageId: selectedMsg.id,
                          accountId: selectedMsg.account_id,
                        })
                      }
                    >
                      <Icon name="forward" size={16} /> {t('mail.forward')}
                    </button>
                    <button type="button" className="rd-rbtn2" disabled title={t('mail.soon')}>
                      <Icon name="delete" size={16} /> {t('mail.folderTrash')}
                    </button>
                    <button type="button" className="rd-rbtn2" disabled title={t('mail.soon')}>
                      <Icon name="schedule" size={16} /> {t('mail.folderSnoozed')}
                    </button>
                    <button
                      type="button"
                      className="rd-rbtn2 ai"
                      disabled={!messageId}
                      onClick={() => openAgent({ question: t('mail.prepareReply') })}
                    >
                      <Icon name="auto_awesome" size={16} /> {t('mail.prepareReply')}
                    </button>
                </div>
                <div
                    id="rd-identity-panel"
                    className={`rd-identity mail-trust${identityOpen ? ' is-open' : ''}`}
                    aria-label={t('reader.trustStrip')}
                    aria-hidden={!identityOpen}
                  >
                    {trustQ.data?.badges?.map((b) => (
                      <span key={b.id} className={`mail-trust-badge ${b.state}`} title={b.detail}>
                        <Icon
                          name={
                            b.state === 'pass'
                              ? 'check'
                              : b.state === 'fail' || b.state === 'warn'
                                ? 'report'
                                : 'info'
                          }
                          size={14}
                        />
                        {b.label}
                      </span>
                    ))}
                    {qualityQ.data?.checked ? (
                      <span className={`mail-trust-badge ${qualityQ.data.ok ? 'pass' : 'warn'}`}>
                        <Icon name={qualityQ.data.ok ? 'check' : 'report'} size={14} />
                        {qualityQ.data.summary}
                      </span>
                    ) : null}
                    {trustQ.data?.note ? (
                      <span className="mail-set-note" style={{ width: '100%', margin: 0 }}>
                        {trustQ.data.note}
                      </span>
                    ) : null}
                    {selectedAcct ? (
                      <div className="rd-identity-images">
                        <div className="rd-identity-images-h">
                          <Icon name="image" size={16} />
                          <strong>{t('mail.imageDisplayTitle')}</strong>
                          <span className="d">
                            {t(`mail.imageDisplay.${accountImageMode}`)}
                            {blockedRemoteCount > 0
                              ? ` · ${t('mail.imageBlockedCount', { count: blockedRemoteCount })}`
                              : ''}
                            {loadRemote ? ` · ${t('mail.imagesLoaded')}` : ''}
                          </span>
                        </div>
                        <ImageDisplaySettings
                          account={selectedAcct}
                          variant="compact"
                          onUpdated={() => {
                            void queryClient.invalidateQueries({ queryKey: ['accounts'] })
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
              </div>

              <div className="rd-viewtabs" role="group" aria-label={t('reader.viewMode')}>
                <button
                  type="button"
                  className={`rd-vt${effectiveView === 'original' ? ' sel' : ''}`}
                  onClick={() => setParam('view', 'original')}
                >
                  {t('reader.original')}
                </button>
                <button
                  type="button"
                  className={`rd-vt${effectiveView === 'translation' ? ' sel' : ''}`}
                  disabled={!goodTranslation}
                  onClick={() => setParam('view', 'translation')}
                >
                  {t('reader.translation')}
                </button>
                <button
                  type="button"
                  className={`rd-vt${effectiveView === 'both' ? ' sel' : ''}`}
                  disabled={!goodTranslation}
                  onClick={() => setParam('view', 'both')}
                >
                  {t('reader.bothStacked')}
                </button>
              </div>

              <div className="rd-scroll">
                <div className="rd-rbody">
                  {noteEditorOpen ? (
                    <div className="rd-note-editor" role="region" aria-label={t('mail.noticeTitle')}>
                      <label className="rd-note-label" htmlFor="rd-note-text">
                        {t('mail.noticeTitle')}
                      </label>
                      <textarea
                        id="rd-note-text"
                        className="rd-note-text"
                        rows={3}
                        maxLength={4000}
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        placeholder={t('mail.noticePlaceholder')}
                      />
                      <div className="rd-note-actions">
                        <button
                          type="button"
                          className="rd-rbtn2"
                          disabled={noteBusy}
                          onClick={() => void saveNoticeText(selectedMsg.id, noteDraft)}
                        >
                          {t('mail.noticeSave')}
                        </button>
                        <button
                          type="button"
                          className="rd-rbtn2"
                          disabled={noteBusy || !selectedMsg.has_note}
                          onClick={() => void saveNoticeText(selectedMsg.id, '')}
                        >
                          {t('mail.noticeRemove')}
                        </button>
                        <button
                          type="button"
                          className="rd-rbtn2"
                          onClick={() => setNoteEditorOpen(false)}
                        >
                          {t('auth.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {showImageBanner ? (
                    <div className="mail-img-notice" role="status">
                      <Icon name="shield" size={16} />
                      <span className="mail-img-acc-lede">
                        {t('reader.imagesBlocked', { count: blockedRemoteCount })}
                      </span>
                      {accountImageMode === 'ask' ? (
                        <>
                          <button
                            type="button"
                            className="mail-tbtn"
                            onClick={() => {
                              if (messageId) allowMessageImagesSession(messageId)
                              if (selectedMsg?.from_addr) allowSenderImagesSession(selectedMsg.from_addr)
                              setLoadRemoteOnce(true)
                            }}
                          >
                            {t('reader.loadImagesOnce')}
                          </button>
                          {selectedMsg.from_addr ? (
                            <button
                              type="button"
                              className="mail-tbtn"
                              onClick={() => {
                                allowSenderImagesAlways(selectedMsg.from_addr!)
                                if (messageId) allowMessageImagesSession(messageId)
                                setLoadRemoteOnce(true)
                              }}
                            >
                              {t('reader.loadImagesAlways', { sender: selectedMsg.from_addr })}
                            </button>
                          ) : null}
                        </>
                      ) : (
                        <span className="d">{t('reader.imagesNeverAccount')}</span>
                      )}
                      {selectedAcct ? (
                        <ImageDisplaySettings
                          account={selectedAcct}
                          variant="compact"
                          onUpdated={async (mode) => {
                            await queryClient.invalidateQueries({ queryKey: ['accounts'] })
                            if (mode === 'always') setLoadRemoteOnce(true)
                            if (mode === 'never') setLoadRemoteOnce(false)
                          }}
                        />
                      ) : null}
                    </div>
                  ) : null}
                  {selectedMsg.body_translated && !goodTranslation ? (
                    <div className="mail-img-notice" role="status">
                      {t('reader.translationRejected')}
                    </div>
                  ) : null}

                  <h1 className="rd-rsubject">{selectedMsg.subject || '(no subject)'}</h1>
                  <div className="mail-sender">
                    <div
                      className="mail-sava"
                      style={{
                        background: selectedAcct
                          ? resolveColor(
                              selectedAcct,
                              Math.max(
                                0,
                                accounts.findIndex((a) => a.id === selectedAcct.id),
                              ),
                            )
                          : 'var(--m-primary)',
                      }}
                    >
                      {(selectedMsg.from_name || selectedMsg.from_addr || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div>
                      <div className="mail-s-n">{selectedMsg.from_name || selectedMsg.from_addr}</div>
                      <div className="mail-s-e">{selectedMsg.from_addr}</div>
                    </div>
                    <div className="mail-sender-meta">
                      <div>{selectedAcct ? resolveLabel(selectedAcct) : ''}</div>
                      <div>
                        {selectedMsg.date_sent ? new Date(selectedMsg.date_sent).toLocaleString() : ''}
                      </div>
                    </div>
                  </div>

                  {threadQ.data && threadQ.data.count > 1 ? (
                    <details className="mail-thread rd-thread-harmony">
                      <summary className="mail-thread-sum">
                        <Icon name="forum" size={16} />
                        <span>{t('reader.threadCount', { count: threadQ.data.count })}</span>
                        <Icon name="expand_more" size={18} className="mail-thread-chev" />
                      </summary>
                      <ul className="mail-thread-list">
                        {threadQ.data.items.map((tm) => (
                          <li key={tm.id}>
                            <button
                              type="button"
                              className={tm.id === messageId ? 'on' : ''}
                              onClick={() => openMessage(tm.id)}
                            >
                              <span>{tm.from_name || tm.from_addr}</span>
                              <span className="muted">{formatWhen(tm.date_sent)}</span>
                              <span>{tm.subject}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}

                  {effectiveView === 'both' ? (
                    <>
                      <div className="rd-block orig">
                        <div className="tag">
                          <Icon name="info" size={13} />
                          {t('reader.original')}
                          {selectedMsg.lang_original ? ` · ${selectedMsg.lang_original.toUpperCase()}` : ''}
                        </div>
                        {bodyPending ? (
                          <p className="d" style={{ marginBottom: 8 }}>
                            {t('reader.bodyLoading')}
                          </p>
                        ) : null}
                        <MailBodyFrame
                          html={originalHtml}
                          messageId={selectedMsg.id}
                          attachments={attachmentsStable}
                          loadRemote={loadRemote}
                          title={t('reader.original')}
                        />
                      </div>
                      <div className="rd-block trans">
                        <div className="tag">
                          <Icon name="translate" size={13} />
                          {t('reader.translation')}
                          {` · ${locale.toUpperCase()}`}
                          {qualityQ.data?.ok ? (
                            <span style={{ color: 'var(--rd-ok)', fontWeight: 600, fontSize: 10 }}>
                              {t('reader.numbersPreserved')}
                            </span>
                          ) : null}
                        </div>
                        {translatedHtml ? (
                          <div
                            className="mail-mailtext"
                            dangerouslySetInnerHTML={{ __html: translatedHtml }}
                          />
                        ) : (
                          <p>{t('reader.noTranslation')}</p>
                        )}
                      </div>
                    </>
                  ) : effectiveView === 'translation' ? (
                    <div className="rd-block trans">
                      <div className="tag">
                        <Icon name="translate" size={13} />
                        {t('reader.translation')}
                        {` · ${locale.toUpperCase()}`}
                        {qualityQ.data?.ok ? (
                          <span style={{ color: 'var(--rd-ok)', fontWeight: 600, fontSize: 10 }}>
                            {t('reader.numbersPreserved')}
                          </span>
                        ) : null}
                      </div>
                      {bodyPending ? (
                        <p className="d" style={{ marginBottom: 8 }}>
                          {t('reader.bodyLoading')}
                        </p>
                      ) : null}
                      {translatedHtml ? (
                        <div
                          className="mail-mailtext"
                          dangerouslySetInnerHTML={{ __html: translatedHtml }}
                        />
                      ) : (
                        <p>{t('reader.noTranslation')}</p>
                      )}
                    </div>
                  ) : (
                    <div className="rd-block orig">
                      <div className="tag">
                        <Icon name="info" size={13} />
                        {t('reader.original')}
                        {selectedMsg.lang_original ? ` · ${selectedMsg.lang_original.toUpperCase()}` : ''}
                      </div>
                      {bodyPending ? (
                        <p className="d" style={{ marginBottom: 8 }}>
                          {t('reader.bodyLoading')}
                        </p>
                      ) : null}
                      <MailBodyFrame
                        html={originalHtml}
                        messageId={selectedMsg.id}
                        attachments={attachmentsStable}
                        loadRemote={loadRemote}
                        title={t('reader.original')}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </section>

        {/* Agent section */}
        <section className={`rd-section${sec === 'agent' ? ' active' : ''}`} data-sec-panel="agent">
          <div className="rd-panel-head">
            <button
              type="button"
              className="rd-back always"
              onClick={closeAgent}
              aria-label={agentReturnTo === 'add' ? t('mail.agentBackToAdd') : t('auth.cancel')}
            >
              <Icon name="arrow_back" size={20} />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2>{t('mail.agentTitle')}</h2>
              <span className="sub">{t('mail.agentReady')}</span>
            </div>
            <button
              type="button"
              className="rd-icon-btn"
              onClick={closeAgent}
              aria-label={t('mail.agentClose')}
              title={t('mail.agentClose')}
            >
              <Icon name="close" size={20} />
            </button>
          </div>
          <div className="rd-scroll">
            {sec === 'agent' ? (
              <div className="rd-agent-stage">
                <div className="rd-agent-hero" aria-hidden={false}>
                  <div className="rd-agent-avatar">
                    <AgentAvatar />
                  </div>
                  <p className="rd-agent-tagline">{t('mail.agentTagline')}</p>
                </div>
                <div className="rd-sec-body rd-agent-body">
                  <div className="rd-card rd-agent-cloak-card">
                    <div style={{ fontSize: '13.5px', lineHeight: 1.55 }}>{t('mail.agentIntro')}</div>
                    <p className="d" style={{ marginTop: 8 }}>
                      {t('mail.agentNote')}
                    </p>
                  </div>
                  {agentAns ? (
                    <div className="rd-card">
                      <h3>{t('mail.agentAnswer')}</h3>
                      <div className="d">{t('mail.agentSuggestion')}</div>
                      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{agentAns}</p>
                      {agentMeta?.why ? (
                        <details style={{ marginTop: 8, fontSize: '0.82em' }}>
                          <summary>{t('mail.agentWhy')}</summary>
                          <p>{agentMeta.why}</p>
                          {agentMeta.location ? (
                            <p className="muted">
                              {t('mail.agentPrivacy')}: {agentMeta.privacy} · {agentMeta.location}
                            </p>
                          ) : null}
                        </details>
                      ) : null}
                      {agentSources.length ? (
                        <ul
                          style={{
                            margin: '8px 0 0',
                            paddingLeft: 18,
                            fontSize: '0.82em',
                            color: 'var(--m-text-2)',
                          }}
                        >
                          {agentSources.map((s, i) => (
                            <li key={`${s.type}-${s.id || i}`}>
                              {s.subject || s.type}
                              {s.id ? ` (${s.id.slice(0, 8)}…)` : ''}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      <p className="mail-set-note">{t('mail.agentActBlocked')}</p>
                    </div>
                  ) : (
                    <div className="rd-card">
                      <h3>{t('mail.agentProposal')}</h3>
                      <div className="d">{t('mail.agentProposalBody')}</div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="rd-rbtn2 ai"
                    disabled={!messageId}
                    onClick={() =>
                      setCompose({
                        mode: 'reply',
                        messageId: messageId!,
                        accountId: selectedMsg?.account_id,
                      })
                    }
                  >
                    <Icon name="auto_awesome" size={16} /> {t('mail.prepareReply')}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
          {sec === 'agent' ? (
          <div className="rd-askbar">
            <input
              value={agentQ}
              onChange={(e) => setAgentQ(e.target.value)}
              placeholder={t('mail.agentAsk')}
              aria-label={t('mail.agentAsk')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void runAgentAsk()
                }
              }}
            />
            <button type="button" disabled={agentBusy || !agentQ.trim()} onClick={() => void runAgentAsk()}>
              <Icon name="send" size={18} />
            </button>
          </div>
          ) : null}
        </section>

        {/* Add account — component provides its own rd-section */}
        {sec === 'add' ? (
          <AddAccountPanel
            onBack={() => setSec('mail')}
            onAskAgent={openConnectHelp}
            onConnected={() => {
              void accountsQ.refetch()
            }}
          />
        ) : null}

        {/* Settings — in-workspace (same drill as Agent) */}
        <section className={`rd-section${sec === 'settings' ? ' active' : ''}`} data-sec-panel="settings">
          <div className="rd-panel-head">
            <button type="button" className="rd-back" onClick={() => setSec('mail')} aria-label={t('auth.cancel')}>
              <Icon name="arrow_back" size={20} />
            </button>
            <div>
              <h2>{t('settings.title')}</h2>
            </div>
          </div>
          <div className="rd-scroll">
            {sec === 'settings' ? (
              <SettingsModal
                embedded
                forceOpen
                onClose={() => setSec('mail')}
                onCustomizeAccount={(id) => openAccountCustomize(id)}
                onAddAccount={() => setSec('add')}
              />
            ) : null}
          </div>
        </section>

        {/* Personal profile */}
        <section className={`rd-section${sec === 'profile' ? ' active' : ''}`} data-sec-panel="profile">
          <div className="rd-panel-head">
            <button type="button" className="rd-back" onClick={() => setSec('mail')} aria-label={t('auth.cancel')}>
              <Icon name="arrow_back" size={20} />
            </button>
            <div>
              <h2>{t('profile.title')}</h2>
              <span className="sub">{t('profile.subtitle')}</span>
            </div>
          </div>
          <div className="rd-scroll">
            {sec === 'profile' ? (
              <ProfilePanel
                avatarRevision={avatarRevision}
                onAvatarChange={() => setAvatarRevision((n) => n + 1)}
              />
            ) : null}
          </div>
        </section>

        {/* Account customize — full section (not popover) */}
        <section className={`rd-section${sec === 'acct' ? ' active' : ''}`} data-sec-panel="acct">
          <div className="rd-panel-head">
            <button
              type="button"
              className="rd-back always"
              onClick={() => setSec('settings')}
              aria-label={t('mail.backToAccounts')}
              title={t('mail.backToAccounts')}
            >
              <Icon name="arrow_back" size={20} />
            </button>
            <div>
              <h2>{t('mail.customizeAccount')}</h2>
              <span className="sub rd-acct-email" title={acctEditAccount?.username || ''}>
                {acctEditAccount?.username ||
                  (acctEditAccount ? resolveLabel(acctEditAccount) : acctId || t('mail.accounts'))}
              </span>
              {acctEditAccount &&
              resolveLabel(acctEditAccount) &&
              resolveLabel(acctEditAccount) !== acctEditAccount.username ? (
                <span className="sub" style={{ display: 'block', marginTop: 2 }}>
                  {resolveLabel(acctEditAccount)}
                </span>
              ) : null}
            </div>
          </div>
          <div className="rd-scroll">
            <div className="rd-sec-body">
              {acctEditAccount ? (
                <div className="rd-card">
                  <p className="rd-acct-working" role="status">
                    {t('mail.workingOnAccount', { email: acctEditAccount.username })}
                  </p>
                  <div className="rd-acct-navrow">
                    <button
                      type="button"
                      className="rd-rbtn2"
                      onClick={() => setSec('settings')}
                    >
                      <Icon name="arrow_back" size={16} />
                      {t('mail.backToAccounts')}
                    </button>
                  </div>

                  <div className="rd-acct-photo" style={{ marginBottom: 16 }}>
                    <p className="d" style={{ marginBottom: 8 }}>
                      {t('mail.accountPhoto')}
                    </p>
                    <div className="rd-profile-ava-row" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <AccountAvatar
                        account={acctEditAccount}
                        color={resolveColor(acctEditAccount, acctEditIdx)}
                        initial={resolveInitial(acctEditAccount)}
                        size={56}
                        revision={acctAvatarRevision}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <label className="rd-rbtn2">
                          <Icon name="upload" size={16} /> {t('mail.uploadAccountPhoto')}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            hidden
                            disabled={acctPhotoBusy}
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null
                              e.target.value = ''
                              if (!file) return
                              setAcctPhotoBusy(true)
                              setAcctPhotoMsg(null)
                              void uploadAccountAvatar(acctEditAccount.id, file)
                                .then(() => {
                                  setAcctAvatarRevision((n) => n + 1)
                                  void accountsQ.refetch()
                                  setAcctPhotoMsg(t('mail.accountPhotoUpdated'))
                                })
                                .catch((err) => {
                                  setAcctPhotoMsg(
                                    err instanceof ApiError
                                      ? err.message
                                      : t('mail.accountPhotoFailed'),
                                  )
                                })
                                .finally(() => setAcctPhotoBusy(false))
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          className="rd-rbtn2"
                          disabled={acctPhotoBusy}
                          onClick={() => {
                            setAcctPhotoBusy(true)
                            setAcctPhotoMsg(null)
                            void deleteAccountAvatar(acctEditAccount.id)
                              .then(() => {
                                setAcctAvatarRevision((n) => n + 1)
                                void accountsQ.refetch()
                                setAcctPhotoMsg(t('mail.accountPhotoRemoved'))
                              })
                              .catch((err) => {
                                setAcctPhotoMsg(
                                  err instanceof ApiError
                                    ? err.message
                                    : t('mail.accountPhotoFailed'),
                                )
                              })
                              .finally(() => setAcctPhotoBusy(false))
                          }}
                        >
                          {t('mail.removeAccountPhoto')}
                        </button>
                      </div>
                    </div>
                    <p className="d" style={{ marginTop: 8 }}>
                      {t('mail.accountPhotoHint')}
                    </p>
                    {acctPhotoMsg ? (
                      <p className="d" role="status" style={{ marginTop: 6 }}>
                        {acctPhotoMsg}
                      </p>
                    ) : null}
                  </div>

                  <label style={{ display: 'block', marginBottom: 12 }}>
                    {t('mail.accountColor')}
                    <input
                      type="color"
                      style={{ display: 'block', marginTop: 6 }}
                      value={
                        (() => {
                          const raw =
                            accountPrefs[acctEditAccount.id]?.color ||
                            (resolveColor(acctEditAccount, acctEditIdx).startsWith('#')
                              ? resolveColor(acctEditAccount, acctEditIdx)
                              : '#6d28d9')
                          return sanitizeAvatarColor(raw, '#6d28d9')
                        })()
                      }
                      onChange={(e) =>
                        setAccountPref(acctEditAccount.id, {
                          color: sanitizeAvatarColor(e.target.value, '#6d28d9'),
                        })
                      }
                    />
                    <p className="d" style={{ marginTop: 6 }}>
                      {t('mail.avatarContrastNote')}
                    </p>
                  </label>
                  <label style={{ display: 'block', marginBottom: 12 }}>
                    {t('mail.accountInitial')}
                    <input
                      type="text"
                      maxLength={2}
                      style={{ display: 'block', marginTop: 6, width: '100%' }}
                      value={accountPrefs[acctEditAccount.id]?.initial ?? ''}
                      placeholder={providerLetter(acctEditAccount)}
                      onChange={(e) =>
                        setAccountPref(acctEditAccount.id, { initial: e.target.value.slice(0, 2) })
                      }
                    />
                  </label>
                  <label style={{ display: 'block', marginBottom: 12 }}>
                    {t('mail.accountLabel')}
                    <input
                      type="text"
                      maxLength={32}
                      style={{ display: 'block', marginTop: 6, width: '100%' }}
                      value={accountPrefs[acctEditAccount.id]?.label ?? ''}
                      placeholder={acctEditAccount.label || acctEditAccount.username}
                      onChange={(e) => setAccountPref(acctEditAccount.id, { label: e.target.value })}
                    />
                  </label>
                  <p className="d">{t('settings.accountsNote')}</p>

                  <ImageDisplaySettings
                    account={acctEditAccount}
                    variant="full"
                    onUpdated={() => {
                      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
                    }}
                  />

                  <div className="rd-acct-sync" style={{ marginTop: 18 }}>
                    <h3 style={{ margin: '0 0 6px', fontSize: 14 }}>{t('mail.accountSyncTitle')}</h3>
                    <p className="d rd-acct-sync-email">
                      {t('mail.syncForAccount', { email: acctEditAccount.username })}
                    </p>
                    <p className="d">{t('mail.accountSyncHint')}</p>
                    <p className="rd-sync-cap-note" role="note">
                      {t('mail.syncInboxCapNote')}
                    </p>
                    {(() => {
                      const p = (acctEditAccount.provider || '').toLowerCase()
                      const oauthCal =
                        p.includes('gmail') ||
                        p.includes('google') ||
                        p.includes('microsoft') ||
                        p.includes('outlook')
                      if (!oauthCal) {
                        return (
                          <p className="d rd-imap-sync-note" role="note">
                            {t('mail.imapNoCalContacts')}
                          </p>
                        )
                      }
                      return (
                        <AccountCalendarConnect
                          accountId={acctEditAccount.id}
                          provider={acctEditAccount.provider}
                        />
                      )
                    })()}
                    <div style={{ marginTop: 12 }}>
                      {(() => {
                        const p = (acctEditAccount.provider || '').toLowerCase()
                        const oauthContacts =
                          p.includes('gmail') ||
                          p.includes('google') ||
                          p.includes('microsoft') ||
                          p.includes('outlook')
                        if (!oauthContacts) {
                          return null
                        }
                        return (
                          <AccountContactsConnect
                            accountId={acctEditAccount.id}
                            provider={acctEditAccount.provider}
                          />
                        )
                      })()}
                    </div>
                  </div>

                  <div className="rd-acct-danger">
                    <button
                      type="button"
                      className="rd-btn-danger"
                      onClick={() => void removeLinkedAccount(acctEditAccount.id)}
                    >
                      <Icon name="delete" size={18} />
                      {t('mail.deleteAccount')}
                    </button>
                    <p className="d">{t('mail.deleteAccountHint')}</p>
                  </div>
                </div>
              ) : (
                <div className="rd-card">
                  <p className="d">{t('settings.accountsNote')}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Calendar — ADR-014 */}
        <section className={`rd-section${sec === 'calendar' ? ' active' : ''}`} data-sec-panel="calendar">
          {sec === 'calendar' ? <CalendarPanel onBack={() => setSec('mail')} /> : null}
        </section>

        {/* Contacts / Imenik — ADR-014 Faza 2 */}
        <section className={`rd-section${sec === 'contacts' ? ' active' : ''}`} data-sec-panel="contacts">
          {sec === 'contacts' ? (
            <ContactsPanel
              onBack={() => setSec('mail')}
              onCompose={(to) => setCompose({ mode: 'new', to })}
              onOpenMessage={(id, aid) => {
                const next = new URLSearchParams(params)
                next.set('sec', 'mail')
                if (aid) next.set('account', aid)
                else next.delete('account')
                const qs = next.toString()
                navigate(`/inbox/${id}${qs ? `?${qs}` : ''}`)
              }}
            />
          ) : null}
        </section>

        {/* Reserved soon (legacy deep-links) */}
        <section className={`rd-section${sec === 'soon' ? ' active' : ''}`} data-sec-panel="soon">
          <div className="rd-panel-head">
            <button type="button" className="rd-back" onClick={() => setSec('mail')} aria-label={t('auth.cancel')}>
              <Icon name="arrow_back" size={20} />
            </button>
            <div>
              <h2>{soonKind === 'contacts' ? t('mail.contacts') : t('mail.calendar')}</h2>
              <span className="sub">{t('mail.soonSlot')}</span>
            </div>
          </div>
          <div className="rd-scroll">
            <div className="rd-reserved">
              <div className="ic">
                <Icon name={soonKind === 'contacts' ? 'contacts' : 'calendar_month'} size={30} />
              </div>
              <h3>
                {soonKind === 'contacts' ? t('mail.contacts') : t('mail.calendar')}
              </h3>
              <p>{t('mail.soonReserved')}</p>
              <button
                type="button"
                className="rd-rbtn2"
                onClick={() => setSec(soonKind === 'contacts' ? 'contacts' : 'calendar')}
              >
                {soonKind === 'contacts' ? t('mail.contacts') : t('mail.calendar')}
              </button>
            </div>
          </div>
        </section>
        </div>
      </div>

      <nav className="rd-bottom" aria-label="Mobile">
        <button
          type="button"
          className={`bn${sec === 'mail' && effectiveDrill === 'home' ? ' active' : ''}`}
          onClick={() => {
            setSec('mail')
            setDrillView('home')
            if (messageId) closeReader()
          }}
        >
          <Icon name="mail" size={22} />
          {t('mail.mailNav')}
        </button>
        <button
          type="button"
          className={`bn${sec === 'mail' && (effectiveDrill === 'list' || effectiveDrill === 'reader') ? ' active' : ''}`}
          onClick={() => {
            setSec('mail')
            setDrillView('list')
            if (messageId) closeReader()
          }}
        >
          <Icon name="list" size={22} />
          {t('mail.listNav')}
        </button>
        <button
          type="button"
          className={`bn${sec === 'contacts' ? ' active' : ''}`}
          onClick={() => setSec('contacts')}
        >
          <Icon name="contacts" size={22} />
          {t('mail.contacts')}
        </button>
        <button
          type="button"
          className={`bn${sec === 'calendar' ? ' active' : ''}`}
          onClick={() => setSec('calendar')}
        >
          <Icon name="calendar_month" size={22} />
          {t('mail.calendar')}
        </button>
        <button
          type="button"
          className={`bn bn-agent${sec === 'agent' ? ' active' : ''}${sec === 'add' ? ' hint' : ''}`}
          aria-pressed={sec === 'agent'}
          title={t('mail.agentTitle')}
          onClick={() => toggleAgent()}
        >
          <Icon name="auto_awesome" size={22} />
          Agent
        </button>
      </nav>
    </div>
  )
}
